import React, { useCallback, useEffect, useState } from 'react';
import { LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { conditionsApi } from '../../lib/api';
import { Modal } from '../ui/Modal';
import { TermsOfUse } from './TermsOfUse';
import { CGU_VERSION } from '../../../shared/conditionsUtilisation.js';

/**
 * Barrière d'acceptation des conditions d'utilisation (demande d'Emeni, 2026-10-14).
 *
 * Posée autour de tout l'espace connecté (cf. Layout dans App.tsx) : tant que la personne n'a pas accepté la
 * version en vigueur, elle ne voit RIEN de l'application — pas même derrière un voile, pour qu'aucun module
 * ne charge de données avant son accord. Une fois acceptée, la question n'est plus posée, jusqu'au prochain
 * changement de CGU_VERSION.
 *
 * Choix de conception :
 *  - la case n'est PAS sur la page de connexion : on ne sait qui se connecte qu'après l'identification, et
 *    redemander à chaque connexion irait contre « une fois cochée, on ne redemande plus » ;
 *  - si le serveur est injoignable pour une autre raison qu'un refus d'authentification, on laisse passer
 *    plutôt que d'enfermer dehors tout le monde pour une panne réseau. L'écran revient au chargement suivant.
 *    La preuve, elle, n'est jamais fabriquée : seul un POST réussi en crée une.
 */
type Etat = 'inconnu' | 'a_accepter' | 'ok';

export const TermsGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, logout, loading } = useAuth();
    const [etat, setEtat] = useState<Etat>('inconnu');
    const [coche, setCoche] = useState(false);
    const [lecture, setLecture] = useState(false);
    const [envoi, setEnvoi] = useState(false);
    const [erreur, setErreur] = useState('');
    const identifiant = user ? `${user.type}:${user.id}` : null;

    useEffect(() => {
        let annule = false;
        if (!identifiant) { setEtat('inconnu'); return; }
        setEtat('inconnu');
        setCoche(false);
        setErreur('');
        conditionsApi.statut()
            .then((s) => { if (!annule) setEtat(s.accepte ? 'ok' : 'a_accepter'); })
            .catch((e: any) => {
                if (annule) return;
                // 401/403 : la session n'est plus valable, le Layout renverra vers /login. Autre erreur : on laisse passer.
                setEtat(e?.status === 401 || e?.status === 403 ? 'inconnu' : 'ok');
            });
        return () => { annule = true; };
    }, [identifiant]);

    const accepter = useCallback(async () => {
        if (!coche || envoi) return;
        setEnvoi(true);
        setErreur('');
        try {
            await conditionsApi.accepter(CGU_VERSION);
            setEtat('ok');
        } catch (e: any) {
            setErreur(e?.message || "Impossible d'enregistrer votre acceptation. Réessayez.");
        } finally {
            setEnvoi(false);
        }
    }, [coche, envoi]);

    // Pas de session (ou chargement) : le Layout gère la redirection vers /login, rien à bloquer ici.
    if (loading || !user) return <>{children}</>;
    if (etat === 'ok') return <>{children}</>;
    if (etat === 'inconnu') {
        return <div className="min-h-screen flex items-center justify-center bg-surface-app"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
    }

    return (
        <div className="min-h-screen bg-surface-app flex items-center justify-center px-4 py-10">
            <div role="dialog" aria-modal="true" aria-labelledby="cgu-titre" className="w-full max-w-[460px] rounded-2xl border border-border bg-surface shadow-2xl p-7 sm:p-8">
                <h1 id="cgu-titre" className="text-[22px] font-semibold leading-tight tracking-[-0.02em] text-content">Avant de continuer</h1>
                <p className="text-[13.5px] text-content-muted mt-2 mb-5">
                    Pour utiliser L'IAmani, vous devez accepter les conditions d'utilisation. Cette question ne vous sera
                    plus posée, sauf si les conditions changent sur le fond.
                </p>

                <label className="flex items-start gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-surface-soft transition-colors">
                    <input
                        type="checkbox"
                        checked={coche}
                        onChange={(e) => setCoche(e.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--brand-color)]"
                    />
                    <span className="text-[13.5px] text-content leading-snug">
                        J'accepte les{' '}
                        <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); setLecture(true); }}
                            className="underline underline-offset-2 hover:text-brand-600"
                        >
                            conditions d'utilisation
                        </button>
                    </span>
                </label>

                {erreur && <p role="alert" className="mt-3 text-[13px] text-state-danger">{erreur}</p>}

                <button
                    type="button"
                    disabled={!coche || envoi}
                    onClick={accepter}
                    className="mt-5 w-full rounded-full bg-content text-surface h-[46px] text-[15px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                >
                    {envoi ? 'Enregistrement…' : 'Continuer'}
                </button>
                <button
                    type="button"
                    onClick={logout}
                    className="mt-3 w-full flex items-center justify-center gap-2 text-[13px] text-content-muted hover:text-content transition-colors"
                >
                    <LogOut size={14} /> Se déconnecter
                </button>
            </div>

            <Modal open={lecture} onClose={() => setLecture(false)} size="xl" title="Conditions d'utilisation">
                <div className="text-[13px] text-content-muted"><TermsOfUse /></div>
            </Modal>
        </div>
    );
};
